import { SiOpenjdk, SiPython, SiJavascript, SiAngular, SiC, SiCplusplus, SiPerl, SiGo } from 'react-icons/si';
import { SignalLow, SignalMedium, SignalHigh, Settings as SettingsIcon } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Code, Save, Loader, AlertTriangle, CheckCircle, Edit3, X, Plus, Trash2, Clock, 
  Search, RotateCcw, AlertCircle, Layers
} from 'lucide-react';
import {
  getCodingQuestions,
  createCodingQuestion,
  updateCodingQuestion,
  deleteCodingQuestion,
  getCodeEvaluationLanguages
} from '../../services/api';
import ConfirmationDialog from '../ConfirmationDialog';
import { Dialog } from '@mui/material';
import { SettingsSection, SelectField } from '../SettingsUIComponents';

const COMPLEXITY_OPTIONS = ['Basic', 'Intermediate', 'Advanced'];
const languageIcons = {
  java: [SiOpenjdk, '#D69D35'], python: [SiPython, '#3776AB'], javascript: [SiJavascript, '#D6B720'], 'java script': [SiJavascript, '#D6B720'], js: [SiJavascript, '#D6B720'], angular: [SiAngular, '#DD0031'], c: [SiC, '#2863A7'], cpp: [SiCplusplus, '#2863A7'], 'c++': [SiCplusplus, '#2863A7'], 'c plus plus': [SiCplusplus, '#2863A7'], perl: [SiPerl, '#39618B'], go: [SiGo, '#00ADD8'], golang: [SiGo, '#00ADD8']
};
const languageOption = lang => {
  const [Icon, color] = languageIcons[lang.toLowerCase()] || [Code, '#667491'];
  return { value: lang, label: lang.charAt(0).toUpperCase() + lang.slice(1), icon: <Icon size={15} style={{ color }} /> };
};
const complexityOption = level => {
  const Icon = { Basic: SignalLow, Intermediate: SignalMedium, Advanced: SignalHigh }[level];
  return { value: level, label: level, icon: <Icon size={16} /> };
};

const SkeletonTable = () => (
  <div className="bg-white border border-[var(--border-color)] rounded-2xl shadow-sm overflow-hidden">
    <div className="p-6 space-y-5 animate-pulse">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-4 w-12 bg-gray-200 rounded"></div>
          <div className="h-4 flex-1 bg-gray-200 rounded"></div>
          <div className="h-6 w-20 bg-gray-200 rounded-lg"></div>
          <div className="h-6 w-24 bg-gray-200 rounded-lg"></div>
          <div className="h-4 w-16 bg-gray-200 rounded"></div>
          <div className="h-4 w-8 bg-gray-200 rounded"></div>
          <div className="h-8 w-20 bg-gray-200 rounded-xl ml-auto"></div>
        </div>
      ))}
    </div>
  </div>
);

const CodingQuestionsSettings = ({ canEditSettings, canViewSettings }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [languages, setLanguages] = useState([]);
  
  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [complexityFilter, setComplexityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  useEffect(() => setPage(1), [searchQuery, languageFilter, complexityFilter]);

  // Modal & Form
  const [openModal, setOpenModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    language: '',
    complexity: 'Basic',
    testCases: [{ input: '', output: '' }],
    timeLimit: 10
  });

  // Confirmation Dialog
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Auto-dismiss success messages
  useEffect(() => {
    if (message.type === 'success') {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Fetch data
  useEffect(() => {
    if (!canViewSettings) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [questionsData, languagesData] = await Promise.all([
          getCodingQuestions(),
          getCodeEvaluationLanguages()
        ]);
        setQuestions(questionsData || []);
        setLanguages((languagesData || []).map(lang => lang.toLowerCase()));
      } catch (error) {
        console.error('Error fetching data:', error);
        setMessage({ type: 'error', text: 'Failed to load coding questions: ' + (error.message || '') });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [canViewSettings]);

  // Filtered Questions (without sorting)
  const filteredQuestions = useMemo(() => {
    let result = [...questions];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(qn => 
        qn.questionId?.toString().toLowerCase().includes(q) ||
        qn.description?.toLowerCase().includes(q) ||
        qn.language?.toLowerCase().includes(q)
      );
    }

    if (languageFilter !== 'all') {
      result = result.filter(qn => qn.language?.toLowerCase() === languageFilter.toLowerCase());
    }

    if (complexityFilter !== 'all') {
      result = result.filter(qn => qn.complexity === complexityFilter);
    }

    return result;
  }, [questions, searchQuery, languageFilter, complexityFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredQuestions.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visibleQuestions = filteredQuestions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const refreshQuestions = async () => {
    try {
      const data = await getCodingQuestions();
      setQuestions(data || []);
    } catch (error) {
      console.error('Error refreshing questions:', error);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingQuestion(null);
    setFormData({
      description: '',
      language: languages[0] || '',
      complexity: 'Basic',
      testCases: [{ input: '', output: '' }],
      timeLimit: 10
    });
    setOpenModal(true);
  };

  const handleOpenEditModal = (question) => {
    setEditingQuestion(question);
    setTimeout(() => {
      setFormData({
        description: question.description || '',
        language: question.language ? question.language.toLowerCase() : (languages[0] || ''),
        complexity: question.complexity || 'Basic',
        testCases: question.testCases && question.testCases.length > 0
          ? question.testCases
          : [{ input: '', output: '' }],
        timeLimit: question.timeLimit || 10
      });
    }, 0);
    setOpenModal(true);
  };

  const handleAddTestCase = () => {
    setFormData({ ...formData, testCases: [...formData.testCases, { input: '', output: '' }] });
  };

  const handleRemoveTestCase = (index) => {
    const newTestCases = formData.testCases.filter((_, i) => i !== index);
    setFormData({ ...formData, testCases: newTestCases });
  };

  const handleTestCaseChange = (index, field, value) => {
    const newTestCases = [...formData.testCases];
    newTestCases[index][field] = value;
    setFormData({ ...formData, testCases: newTestCases });
  };

  const handleSaveQuestion = async () => {
    if (!canEditSettings) return;
    if (!formData.description || !formData.language || !formData.complexity) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }
    try {
      setLoading(true);
      const dataToSave = { ...formData, language: formData.language.toLowerCase() };
      if (editingQuestion) {
        await updateCodingQuestion(editingQuestion.questionId, dataToSave);
        setMessage({ type: 'success', text: 'Question updated successfully!' });
      } else {
        await createCodingQuestion(dataToSave);
        setMessage({ type: 'success', text: 'Question created successfully!' });
      }
      await refreshQuestions();
      setOpenModal(false);
    } catch (error) {
      console.error('Error saving question:', error);
      setMessage({ type: 'error', text: 'Failed to save question: ' + (error.message || '') });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = (question) => {
    if (!canEditSettings) return;
    setConfirmationDialog({
      open: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to delete the question "${question.description}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          setLoading(true);
          await deleteCodingQuestion(question.questionId);
          setMessage({ type: 'success', text: 'Question deleted successfully!' });
          await refreshQuestions();
        } catch (error) {
          console.error('Error deleting question:', error);
          setMessage({ type: 'error', text: 'Failed to delete question: ' + (error.message || '') });
        } finally {
          setLoading(false);
          setConfirmationDialog({ ...confirmationDialog, open: false });
        }
      }
    });
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'Basic': return 'bg-green-50 text-green-700 border border-green-200';
      case 'Intermediate': return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'Advanced': return 'bg-red-50 text-red-700 border border-red-200';
      default: return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setLanguageFilter('all');
    setComplexityFilter('all');
  };

  return (
    <>
      <SettingsSection icon={Code} title="Coding Questions">
      {/* Filter Toolbar with Dynamic Count and Add Button */}
      <div className="settings-coding-toolbar">
        <div className="relative flex-1 min-w-0 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID, description, or language..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-sm text-[var(--text-primary)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)] transition-all"
          />
        </div>

        <SelectField label="Language" value={languageFilter} onChange={e => setLanguageFilter(e.target.value)} options={[{value: 'all', label: 'All Languages'}, ...languages.map(languageOption)]} />
        <SelectField label="Complexity" value={complexityFilter} onChange={e => setComplexityFilter(e.target.value)} options={[{value: 'all', label: 'All Levels'}, ...COMPLEXITY_OPTIONS.map(complexityOption)]} />

        <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[var(--border-color)] rounded-xl shadow-sm whitespace-nowrap">
          <Layers className="w-4 h-4 text-[var(--primary)]" />
          <span className="text-sm font-medium text-[var(--text-secondary)]">
            {filteredQuestions.length} {filteredQuestions.length === 1 ? 'Question' : 'Questions'}
          </span>
        </div>

        {canEditSettings && (
          <button
            onClick={handleOpenCreateModal}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white text-sm font-semibold rounded-xl shadow-md shadow-[var(--primary)]/30 hover:shadow-lg hover:shadow-[var(--primary)]/40 transition-all hover:scale-[1.02] active:scale-[0.98] w-full lg:w-auto justify-center"
          >
            <Plus className="w-4 h-4" />
            Add Coding Question
          </button>
        )}

        <button
          onClick={resetFilters}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-[var(--border-color)] text-[var(--text-secondary)] text-sm font-medium rounded-xl hover:bg-[var(--bg-page)] hover:text-[var(--text-primary)] transition-all w-full lg:w-auto"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      {/* Alerts */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 border transition-all ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
          <p className="text-sm font-medium flex-1">{message.text}</p>
          <button onClick={() => setMessage({ type: '', text: '' })} className="p-1 hover:bg-black/5 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Questions Table / Empty State / Skeleton */}
      {loading && !questions.length ? (
        <SkeletonTable />
      ) : filteredQuestions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-[var(--border-color)] rounded-2xl border-dashed">
          <div className="w-16 h-16 bg-[var(--bg-page)] rounded-2xl flex items-center justify-center mb-4">
            <Code className="w-8 h-8 text-[var(--primary)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">No Coding Questions Found</h3>
          <p className="text-[var(--text-secondary)] text-sm mb-6 text-center max-w-sm">
            {searchQuery || languageFilter !== 'all' || complexityFilter !== 'all' 
              ? "Try adjusting your filters or search query to find what you're looking for." 
              : "Get started by creating your first coding question for your interviews."}
          </p>
          {canEditSettings && !(searchQuery || languageFilter !== 'all' || complexityFilter !== 'all') && (
            <button
              onClick={handleOpenCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white text-sm font-semibold rounded-xl shadow-md shadow-[var(--primary)]/30 hover:shadow-lg transition-all"
            >
              <Plus className="w-4 h-4" />
              Add Coding Question
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[var(--border-color)] rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[var(--bg-page)] border-b border-[var(--border-color)]">
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider w-1/3">Question</th>
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Language</th>
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Complexity</th>
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Time Limit</th>
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Tests</th>
                  <th className="px-6 py-4 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-color)]">
                {visibleQuestions.map((question) => (
                  <tr key={question.questionId} className="hover:bg-[var(--bg-page)]/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-mono font-semibold text-[var(--text-muted)]">
                      #{question.questionId}
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-primary)] font-medium">
                      <div className="line-clamp-2 max-w-md" title={question.description}>
                        {question.description}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                        {question.language}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${getComplexityColor(question.complexity)}`}>
                        {question.complexity}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        <span>{question.timeLimit} min</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">
                      <span className="settings-tests-count">{question.testCases?.length || 0}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {canEditSettings && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEditModal(question)}
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-[var(--bg-page)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--primary-light)] hover:text-[var(--primary)] transition-all"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question)}
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filteredQuestions.length > 0 && <div className="settings-pagination">
        <span>Showing {(currentPage - 1) * pageSize + 1}&#8211;{Math.min(currentPage * pageSize, filteredQuestions.length)} of {filteredQuestions.length} results</span>
        <div><button type="button" aria-label="Previous page" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>&#8249;</button>
        {Array.from({ length: pageCount }, (_, index) => index + 1).filter(number => number === 1 || number === pageCount || Math.abs(number - currentPage) <= 1).map((number, index, numbers) => <React.Fragment key={number}>{index > 0 && number - numbers[index - 1] > 1 && <span>&#8230;</span>}<button type="button" aria-label={`Page ${number}`} aria-current={number === currentPage ? 'page' : undefined} onClick={() => setPage(number)}>{number}</button></React.Fragment>)}
        <button type="button" aria-label="Next page" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>&#8250;</button></div>
      </div>}
      </SettingsSection>
      {/* Create/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="settings-question-dialog-title"
        PaperProps={{ className: 'settings-question-dialog' }}
      >
        <header className="settings-question-dialog-header">
          <span className="settings-question-dialog-icon"><SettingsIcon size={23} /></span>
          <h2 id="settings-question-dialog-title">{editingQuestion ? 'Edit Question' : 'Add New Question'}</h2>
          <button type="button" aria-label="Close question form" onClick={() => setOpenModal(false)}><X size={20} /></button>
        </header>
        <div className="settings-question-dialog-body">
          {message.type === "error" && message.text && <div className="settings-question-error" role="alert"><AlertCircle size={16} />{message.text}</div>}
          <h3>Question Information</h3>
          <label className="settings-question-description">Description
            <textarea rows={4} placeholder="Write your question description here..." value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
          </label>
          <div className="settings-question-information-fields">
            <SelectField label="Language" value={formData.language} onChange={e => setFormData({ ...formData, language: e.target.value })} options={languages.map(languageOption)} />
            <SelectField label="Complexity" value={formData.complexity} onChange={e => setFormData({ ...formData, complexity: e.target.value })} options={COMPLEXITY_OPTIONS.map(complexityOption)} />
            <label>Time Limit (min)<input type="number" min="1" value={formData.timeLimit} onChange={e => setFormData({ ...formData, timeLimit: Number(e.target.value) })} /></label>
          </div>
          <div className="settings-question-tests-heading"><h3>Test Cases</h3><button type="button" onClick={handleAddTestCase}><Plus size={16} />Add Test Case</button></div>
          <div className="settings-question-tests">
            {formData.testCases.map((tc, idx) => <div className="settings-question-test" key={idx}>
              <div className="settings-question-test-heading"><span>{idx + 1}</span><h4>Test Case #{idx + 1}</h4>{formData.testCases.length > 1 && <button type="button" aria-label={`Remove test case ${idx + 1}`} onClick={() => handleRemoveTestCase(idx)}><Trash2 size={17} /></button>}</div>
              <div className="settings-question-test-fields">
                <label>Input<textarea rows={3} value={tc.input} onChange={e => handleTestCaseChange(idx, 'input', e.target.value)} placeholder="e.g., [1, 2, 3]" /></label>
                <label>Expected Output<textarea rows={3} value={tc.output} onChange={e => handleTestCaseChange(idx, 'output', e.target.value)} placeholder="e.g., 6" /></label>
              </div>
            </div>)}
          </div>
        </div>
        <footer className="settings-question-dialog-footer">
          <button type="button" onClick={() => setOpenModal(false)}><X size={15} />Cancel</button>
          <button type="button" onClick={handleSaveQuestion} disabled={loading}>{loading ? <Loader size={16} className="animate-spin" /> : <CheckCircle size={16} />}{loading ? 'Saving...' : 'Save Questions'}</button>
        </footer>
      </Dialog>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ ...confirmationDialog, open: false })}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />
    </>
  );
};

export default CodingQuestionsSettings;
